import { useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'

/**
 * Custom hook that manages paginated state synced with URL search params.
 * Handles conversion between 0-indexed internal state and 1-indexed URL display.
 *
 * @param urlParamKey - The URL search param key (defaults to 'page')
 * @returns [page, setPage] - 0-indexed page state and setter
 */
export const usePaginatedState = (urlParamKey = 'page'): [number, (page: number) => void] => {
  const [searchParams, setSearchParams] = useSearchParams()
  const location = useLocation()

  // Convert 1-indexed URL to 0-indexed internal state
  const getPageFromUrl = (): number => {
    const urlPage = searchParams.get(urlParamKey)
    return urlPage !== null ? Math.max(0, parseInt(urlPage, 10) - 1) : 0
  }

  const [page, setPageInternal] = useState<number>(getPageFromUrl)

  // Wrapper that updates both state and URL
  const setPage = (newPage: number) => {
    setPageInternal(newPage)

    const newParams = new URLSearchParams(searchParams)
    if (newPage === 0) {
      // First page doesn't need a URL param
      newParams.delete(urlParamKey)
    } else {
      // Convert 0-indexed to 1-indexed for URL display
      newParams.set(urlParamKey, (newPage + 1).toString())
    }
    // Preserve location.state when updating search params
    setSearchParams(newParams, { replace: true, state: location.state })
  }

  return [page, setPage]
}
